import React from "react";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { CODE_DATA } from "../../facility-management-screen/constants";
import Input from "../../../component-lib/Atoms/Input/Input";
import Button from "../../../component-lib/Molecules/button/Button";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";
import BootstrapDialog from "../../../utils/bootstrap-dialog-style/BootstrapDialog";

interface CodeDialogProps {
  isOpen: boolean;
  handleData: (data: any) => void;
  handleClose: () => void;
  isEdit?: boolean;
  code?: string;
  defaultCodeData: any;
  codeType?: "ICD10" | "DSM";
}

export default function CodeDialog({
  isOpen = false,
  handleClose,
  handleData,
  isEdit = false,
  code,
  defaultCodeData,
  codeType = "DSM",
}: CodeDialogProps) {
  const dialogHeading = isEdit ? `Edit ${code}` : `Add ${code}`;

  // All fields are mandatory for both ICD10 and DSM codes.
  const validationSchema = Yup.object().shape({
    [CODE_DATA?.CODE_NAME]: Yup.string()
      .max(20, `${code} should not exceed 20 characters`)
      .required(`${code} is required`),

    [CODE_DATA?.DESCRIPTION]: Yup.string().required("Description is required"),
    // Diagnosis Name is optional for both ICD10 and DSM codes.
    [CODE_DATA?.DIAGNOSIS_NAME]: Yup.string(),
  });

  return (
    <BootstrapDialog
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      aria-labelledby="customized-dialog-title"
      open={isOpen}
    >
      <Formik
        initialValues={{
          [CODE_DATA?.CODE_NAME]: `${defaultCodeData?.codeName || ""}`,
          [CODE_DATA?.DESCRIPTION]: `${defaultCodeData?.description || ""}`,
          [CODE_DATA?.DIAGNOSIS_NAME]: `${defaultCodeData?.diagnosisName || ""}`,
        }}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={(values, { setSubmitting }) => {
          try {
            // getFormData(values);
            handleData(values);
          } catch (error) {
            console.log(error);
          } finally {
            setSubmitting(false); // Reset submitting state
          }
        }}
      >
        {({
          errors,
          values,
          touched,
          isValid,
          dirty,
          setFieldValue,
          handleBlur,
          handleSubmit,
        }) => {
          return (
            <Form onSubmit={handleSubmit}>
              <DialogTitle
                sx={{
                  m: 0,
                  p: 2,
                  fontWeight: 600,
                  fontSize: "18px",
                  fontFamily: "inter",
                }}
                id="customized-dialog-title"
              >
                {dialogHeading}
              </DialogTitle>
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={(theme) => ({
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: theme.palette.grey[500],
                })}
              >
                <CloseIcon />
              </IconButton>
              <hr />
              {/* MAIN CONTENT */}
              <DialogContent>
                {/* <div className="pt-4">
                    <EventEHRUploadImageCard
                      imageUrl={null}
                      handleImage={(data) => {}}
                    />
                  </div> */}

                <div
                  style={{
                    // height: deviceType === "mobile" ? "40vh" : "fit-content",
                    overflow: "auto",
                    display: "grid",
                    gridTemplateColumns: "1fr",
                    gap: "16px",
                  }}
                >
                  <div className="pt-4">
                    <Input
                      textFieldLabel={code}
                      // placeholder=""
                      maxLength={20}
                      size="small"
                      name={CODE_DATA.CODE_NAME}
                      value={values[CODE_DATA.CODE_NAME]}
                      onChangeCb={(e) => {
                        setFieldValue(CODE_DATA.CODE_NAME, e.target.value);
                      }}
                      onBlur={handleBlur}
                      error={
                        touched[CODE_DATA.CODE_NAME] &&
                        Boolean(errors[CODE_DATA.CODE_NAME])
                      }
                      helperText={
                        touched[CODE_DATA.CODE_NAME]
                          ? errors[CODE_DATA.CODE_NAME]
                          : undefined
                      }
                    />
                  </div>


                  <Input
                    textFieldLabel="Diagnosis Name"
                    size="small"
                    name={CODE_DATA.DIAGNOSIS_NAME}
                    value={values[CODE_DATA.DIAGNOSIS_NAME]}
                    onChangeCb={(e) => {
                      setFieldValue(CODE_DATA.DIAGNOSIS_NAME, e.target.value);
                    }}
                    onBlur={handleBlur}
                    error={
                      touched[CODE_DATA.DIAGNOSIS_NAME] &&
                      Boolean(errors[CODE_DATA.DIAGNOSIS_NAME])
                    }
                    helperText={
                      touched[CODE_DATA.DIAGNOSIS_NAME]
                        ? errors[CODE_DATA.DIAGNOSIS_NAME]
                        : undefined
                    }
                  />

                  <Input
                    textFieldLabel="Description"
                    // placeholder=""
                    multiline={true}
                    maxWords={250}
                    rows={4}
                    size="small"
                    name={CODE_DATA.DESCRIPTION}
                    value={values[CODE_DATA.DESCRIPTION]}
                    onChangeCb={(e) => {
                      setFieldValue(CODE_DATA.DESCRIPTION, e.target.value);
                    }}
                    onBlur={handleBlur}
                    error={
                      touched[CODE_DATA.DESCRIPTION] &&
                      Boolean(errors[CODE_DATA.DESCRIPTION])
                    }
                    helperText={
                      touched[CODE_DATA.DESCRIPTION]
                        ? errors[CODE_DATA.DESCRIPTION]
                        : undefined
                    }
                  />
                </div>
              </DialogContent>

              {/* ACTION BUTTON */}
              <DialogActions style={{ padding: "24px" }}>
                {/* <div className="flex justify-end mt-10 gap-4"> */}
                {isEdit && (
                  <Button
                    label={TEXT_CONSTANTS?.CANCEL}
                    variant="outlined"
                    onClickCb={handleClose}
                    style={{
                      width: "120px",
                      height: "44px",
                      padding: "12px 20px",
                      fontSize: "16px",
                      backgroundColor: "white",
                      border: "none",
                      color: "#393939",
                    }}
                  />
                )}
                <Button
                  //   disabled={!isValid} // Button disabled if form is invalid or submitting
                  type="submit"
                  label={TEXT_CONSTANTS?.SAVE}
                  variant="contained"
                  onClickCb={() => {}}
                  style={{
                    width: "200px",
                    height: "44px",
                    padding: "12px 20px",
                    fontSize: "16px",
                  }}
                ></Button>
                {/* </div> */}
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </BootstrapDialog>
  );
}
